import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { ActionButtonGroup } from 'components/forms';
import { showAlert } from 'utils/toastBridge';
import {
  ENV_NAV_GROUPS,
  ENV_MODULES,
  ENV_STATUS_META,
  DEFAULT_MODULE_ID,
} from 'system/envSettingsConfig';
import { loadEnvSettings, loadEnvSettingsAsync, saveEnvSettings } from 'system/envSettingsStore';
import {
  loadTenantAppearance,
  saveTenantAppearance,
} from 'system/appearanceStore';
import { defaultAppearanceSettings } from 'styles/appearanceSlots';
import AppearanceThemePanel from 'pages/system/AppearanceThemePanel';
import TenantLogoUploadPanel from 'pages/system/TenantLogoUploadPanel';
import SealSettingsPanel from 'pages/system/SealSettingsPanel';
import FormRegistryPanel from 'pages/system/FormRegistryPanel';
import FormMatchRulesPanel from 'pages/system/FormMatchRulesPanel';
import FormAclPanel from 'pages/system/FormAclPanel';
import ReportCatalogPanel from 'pages/system/ReportCatalogPanel';
import { useErpThemeOptional } from 'contexts/ErpThemeContext';

/** 노션형 환경설정 — 좌측 모듈 트리 + 우측 항목 */

const Shell = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  height: 100%;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
`;

const PageWrap = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

const Side = styled.aside`
  flex: 0 0 220px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid ${theme.colors.border};
  background: ${theme.colors.background.septenary};
`;

const SideHead = styled.div`
  padding: 14px 14px 10px;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  border-bottom: 1px solid ${theme.colors.border};
`;

const SideScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 8px 6px 12px;
`;

const NavGroupLabel = styled.div`
  margin: 10px 8px 4px;
  font-size: ${theme.typography.fontSize.xxs};
  font-weight: 600;
  color: ${theme.colors.text.tertiary};
  letter-spacing: 0.02em;
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 7px 10px;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  background: ${(p) => (p.$active ? theme.colors.background.medium : 'transparent')};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  text-align: left;
  cursor: pointer;
  &:hover {
    background: ${theme.colors.background.medium};
  }
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
`;

const TenantHint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
`;

const ContentScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 20px 28px 32px;
`;

const PageHead = styled.div`
  margin-bottom: 22px;
`;

const PageTitle = styled.h2`
  margin: 0 0 6px;
  font-size: ${theme.typography.fontSize.xl};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  line-height: 1.3;
`;

const PageDesc = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
  max-width: 640px;
`;

const Section = styled.section`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: ${theme.typography.fontSize.md};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const FieldList = styled.div`
  border-top: 1px solid ${theme.colors.border};
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 14px 0;
  border-bottom: 1px solid ${theme.colors.border};
`;

const FieldMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FieldLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xxs};
  font-weight: 600;
  line-height: 1.4;
  color: ${(p) => {
    if (p.$status === 'linked') return theme.colors.status.successStrong;
    if (p.$status === 'stored') return theme.colors.status.warningStrong;
    return theme.colors.text.tertiary;
  }};
  background: ${(p) => {
    if (p.$status === 'linked') return theme.colors.primaryLight;
    if (p.$status === 'stored') return theme.colors.status.warning + '33';
    return theme.colors.background.medium;
  }};
`;

const FieldDesc = styled.div`
  margin-top: 4px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  line-height: 1.45;
`;

const FieldControl = styled.div`
  flex: 0 0 auto;
  min-width: 180px;
  max-width: 280px;
  display: flex;
  justify-content: flex-end;
`;

const Select = styled.select`
  width: 100%;
  min-width: 160px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.white};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
`;

const TextInput = styled.input`
  width: 100%;
  min-width: 160px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.white};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
`;

const Toggle = styled.button`
  position: relative;
  width: 40px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: ${theme.borderRadius.pill};
  background: ${(p) => (p.$on ? theme.colors.primary : theme.colors.senary)};
  cursor: pointer;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? '20px' : '2px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${theme.colors.white};
    transition: left 0.12s ease;
  }
`;

const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  min-width: 160px;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  cursor: pointer;
`;

const NoteBox = styled.div`
  width: 100%;
  max-width: 280px;
  padding: 8px 10px;
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.background.light};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.45;
`;

function StatusPill({ status }) {
  const meta = ENV_STATUS_META[status] || ENV_STATUS_META.info;
  return (
    <StatusBadge $status={status} title={meta.title}>
      {meta.label}
    </StatusBadge>
  );
}

function FieldControlView({ field, value, onChange }) {
  if (field.control === 'note') {
    return <NoteBox>설정값 없음</NoteBox>;
  }
  if (field.control === 'select') {
    return (
      <Select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        title={field.description}
      >
        {(field.options || []).map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
    );
  }
  if (field.control === 'text') {
    return (
      <TextInput
        type="text"
        value={value ?? ''}
        placeholder={field.placeholder || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.control === 'number') {
    return (
      <TextInput
        type="number"
        min={field.min}
        max={field.max}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
    );
  }
  if (field.control === 'toggle') {
    const on = !!value;
    return (
      <Toggle
        type="button"
        $on={on}
        aria-pressed={on}
        title={on ? '켜짐' : '꺼짐'}
        onClick={() => onChange(!on)}
      />
    );
  }
  if (field.control === 'multiCheck') {
    const selected = new Set(Array.isArray(value) ? value.map(String) : []);
    return (
      <CheckList>
        {(field.options || []).map((opt) => {
          const checked = selected.has(String(opt.value));
          return (
            <CheckLabel key={opt.value}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  const next = new Set(selected);
                  if (checked) next.delete(String(opt.value));
                  else next.add(String(opt.value));
                  onChange([...next]);
                }}
              />
              {opt.label}
            </CheckLabel>
          );
        })}
      </CheckList>
    );
  }
  return null;
}

const EMBEDDED_MODULE_IDS = new Set([
  'logo',
  'seal',
  'formRegistry',
  'formMatch',
  'formAcl',
  'reportCatalog',
]);

const EnvSettings = () => {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const erpTheme = useErpThemeOptional();
  const [moduleId, setModuleId] = useState(DEFAULT_MODULE_ID);
  const [values, setValues] = useState(() => loadEnvSettings(tenantCode));
  const [appearance, setAppearance] = useState(() => loadTenantAppearance(tenantCode));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await loadEnvSettingsAsync(tenantCode);
        if (!cancelled) {
          setValues(next);
          setAppearance(loadTenantAppearance(tenantCode));
          setDirty(false);
        }
      } catch (e) {
        console.warn('[EnvSettings] load', e);
        if (!cancelled) {
          setValues(loadEnvSettings(tenantCode));
          setAppearance(loadTenantAppearance(tenantCode));
          setDirty(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  const navGroups = useMemo(
    () =>
      ENV_NAV_GROUPS.map((grp) => ({
        ...grp,
        items: (grp.items || []).filter((item) => !item.masterOnly || isMaster),
      })).filter((grp) => (grp.items || []).length > 0),
    [isMaster]
  );

  useEffect(() => {
    const visible = navGroups.some((g) => g.items.some((i) => i.id === moduleId));
    if (!visible) setModuleId(DEFAULT_MODULE_ID);
  }, [navGroups, moduleId]);

  const moduleDef = ENV_MODULES[moduleId] || ENV_MODULES[DEFAULT_MODULE_ID];

  const setField = useCallback((modId, key, nextVal) => {
    setValues((prev) => ({
      ...prev,
      [modId]: {
        ...(prev[modId] || {}),
        [key]: nextVal,
      },
    }));
    setDirty(true);
  }, []);

  const handleAppearanceChange = useCallback((next) => {
    setAppearance({ ...defaultAppearanceSettings(), ...next });
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!tenantCode) {
      showAlert('기관코드가 없습니다. 다시 로그인해 주세요.');
      return;
    }
    if (moduleId === 'appearance') {
      const saved = saveTenantAppearance(tenantCode, appearance);
      setAppearance(saved);
      erpTheme?.reload?.();
      setDirty(false);
      showAlert('표시·테마·글꼴 설정을 저장했습니다.');
      return;
    }
    try {
      const saved = await saveEnvSettings(tenantCode, values);
      setValues(saved);
      setDirty(false);
      showAlert('환경설정을 저장했습니다.');
    } catch (e) {
      showAlert(e?.message || '환경설정 저장에 실패했습니다.');
    }
  }, [tenantCode, moduleId, appearance, values, erpTheme]);

  const actionButtons = useMemo(
    () => [
      {
        id: 'save',
        label: '저장',
        variant: 'primary',
        hotkey: 'F10',
        title: '저장 (F10)',
        onClick: handleSave,
        disabled: !dirty,
      },
    ],
    [handleSave, dirty]
  );

  return (
    <PageWrap>
      <Shell>
        <Side>
          <SideHead>환경설정</SideHead>
          <SideScroll>
            {navGroups.map((grp) => (
              <div key={grp.id}>
                <NavGroupLabel>{grp.label}</NavGroupLabel>
                {grp.items.map((item) => (
                  <NavItem
                    key={item.id}
                    type="button"
                    $active={moduleId === item.id}
                    onClick={() => setModuleId(item.id)}
                  >
                    {item.label}
                  </NavItem>
                ))}
              </div>
            ))}
          </SideScroll>
        </Side>

        <Main>
          <TopBar>
            <TenantHint>
              기관 {tenantCode || '—'}
              {user?.id ? ` · ${user.id}` : ''}
              {!EMBEDDED_MODULE_IDS.has(moduleId) && dirty ? ' · 변경사항 있음' : ''}
            </TenantHint>
            {!EMBEDDED_MODULE_IDS.has(moduleId) ? (
              <ActionButtonGroup buttons={actionButtons} />
            ) : null}
          </TopBar>

          <ContentScroll>
            <PageHead>
              <PageTitle>{moduleDef.title}</PageTitle>
              <PageDesc>{moduleDef.description}</PageDesc>
            </PageHead>

            {moduleId === 'appearance' ? (
              <AppearanceThemePanel
                value={appearance}
                onChange={handleAppearanceChange}
                scope="tenant"
              />
            ) : moduleId === 'logo' ? (
              <TenantLogoUploadPanel tenantCode={tenantCode} />
            ) : moduleId === 'seal' ? (
              <SealSettingsPanel tenantCode={tenantCode} />
            ) : moduleId === 'formRegistry' ? (
              <FormRegistryPanel embedded />
            ) : moduleId === 'formMatch' ? (
              <FormMatchRulesPanel />
            ) : moduleId === 'formAcl' ? (
              <FormAclPanel />
            ) : moduleId === 'reportCatalog' ? (
              <ReportCatalogPanel />
            ) : (
              (moduleDef.sections || []).map((sec) => (
                <Section key={sec.id}>
                  <SectionTitle>{sec.title}</SectionTitle>
                  <FieldList>
                    {(sec.fields || []).map((field) => (
                      <FieldRow key={field.id}>
                        <FieldMeta>
                          <FieldLabelRow>
                            <FieldLabel>{field.label}</FieldLabel>
                            <StatusPill status={field.status} />
                          </FieldLabelRow>
                          {field.description ? <FieldDesc>{field.description}</FieldDesc> : null}
                        </FieldMeta>
                        <FieldControl>
                          <FieldControlView
                            field={field}
                            value={values?.[moduleId]?.[field.key]}
                            onChange={(v) => {
                              if (field.control === 'note') return;
                              setField(moduleId, field.key, v);
                            }}
                          />
                        </FieldControl>
                      </FieldRow>
                    ))}
                  </FieldList>
                </Section>
              ))
            )}
          </ContentScroll>
        </Main>
      </Shell>
    </PageWrap>
  );
};

export default EnvSettings;
